import React, { memo, useCallback, useMemo, type SyntheticEvent } from 'react';
import type { BusinessIssueField } from '@atlassian/jira-business-entity-project/src/services/issue-types-and-fields/index.tsx';
import { useExpandCollapseAllItems } from '@atlassian/jira-business-expand-collapse-all-items-controller';
import { useHideDoneItems } from '@atlassian/jira-business-filters/src/controllers/hide-done-items/index.tsx';
import { useCanCreateIssueInStatus } from '@atlassian/jira-business-issue-create/src/controllers/can-create-issue-in-status/index.tsx';
import { useHierarchyStore } from '@atlassian/jira-business-list/src/controllers/hierarchy/hierarchy-store/index.tsx';
import {
	StatusCategoryTypes,
	StatusCategoryIds,
} from '@atlassian/jira-common-constants/src/status-categories';
import { useIntl } from '@atlassian/jira-intl';
import { getAriConfig } from '@atlassian/jira-platform-ari';
import {
	CATEGORY_TYPE,
	ASSIGNEE_TYPE,
	PRIORITY_TYPE,
	STATUS_TYPE,
} from '@atlassian/jira-platform-field-config';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { toIssueId } from '@atlassian/jira-shared-types/src/general.tsx';
import CreateIssuePlusButton from '../../../common/ui/create-issue-plus-button';
import { useGetGroupByField } from '../../../controllers/get-group-by-field';
import { useIssueFields } from '../../../controllers/get-issue-fields';
import { useGroupBy } from '../../../controllers/group-by';
import type { ListViewControllerData } from '../../../controllers/list-view-issues/types';
import { useRowCreateStore } from '../../../controllers/row-create';
import { AssigneeItem } from './assignee-item';
import { CategoryItem } from './category-item';
import { ExpandIcon } from './expand-icon';
import messages from './messages';
import { PriorityItem } from './priority-item';
import { SelectGroupCheckbox } from './select-group-checkbox';
import { SprintItem } from './sprint-item';
import { StatusItem } from './status-item';
import { StoryPointsItem } from './storypoints-item';
import {
	Wrapper,
	GroupContainer,
	LoadingRow,
	LoadingIcon,
	LoadingWrapper,
	LoadingField,
	CreateIssuePlusButtonWrapper,
} from './styled';

type GroupItemProps = {
	data: ListViewControllerData;
	rowIndex?: number;
	canCreateIssues?: boolean;
};

const GroupItemRenderer = ({
	field,
	data,
}: {
	field: BusinessIssueField;
	data: ListViewControllerData;
}) => {
	switch (field.__typename) {
		case 'JiraSingleSelectUserPickerField':
			if (field.type === ASSIGNEE_TYPE) {
				return <AssigneeItem data={data} />;
			}
			return null;
		case 'GenericIssueField':
			if (field.type === STATUS_TYPE) {
				return <StatusItem data={data} />;
			}
			return null;
		case 'JiraPriorityField':
			return <PriorityItem data={data} />;
		case 'JWMCategoryField':
			return <CategoryItem data={data} />;
		case 'JiraNumberField':
			if (field.aliasFieldId === 'storypoints') {
				return <StoryPointsItem value={data.storypoints} />;
			}
			if (field.aliasFieldId === 'storypointestimate') {
				return <StoryPointsItem value={data.storypointestimate} />;
			}
			return null;
		case 'JiraSprintField':
			return <SprintItem data={data} />;
		default:
			return null;
	}
};

export const GroupItem = memo<GroupItemProps>(
	({ data, rowIndex, canCreateIssues }: GroupItemProps) => {
		const [groupByFieldId] = useGroupBy();
		const [, setHideDoneItems] = useHideDoneItems();
		const issueId = toIssueId(data.issueId);
		const totalGroupItems = data.childSummary?.totalCount ?? 0;
		const { formatMessage } = useIntl();
		const [
			{
				expandedItems: expandedGroups,
				hierarchyData: { loading },
			},
			{ setExpandedItems: setGroupExpanded },
		] = useHierarchyStore();
		const [{ shouldExpandAllItems }, { disableExpandCollapseAllItems }] = useExpandCollapseAllItems(
			{ view: 'list' },
		);

		const [, { setRowCreatePosition }] = useRowCreateStore();
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const { getAvailableFields } = useIssueFields();
		const availableFields = getAvailableFields();
		const categoryFieldId = availableFields.find(
			(fieldItem) => fieldItem.type === CATEGORY_TYPE,
		)?.id;

		const field = useGetGroupByField();

		const onExpand = useCallback(() => {
			const isExpanded = Boolean(expandedGroups[issueId]);
			const isStatusGroup = groupByFieldId === STATUS_TYPE;
			const isDoneStatus =
				// @ts-expect-error - TS2367 - This condition will always return 'false' since the types 'string' and 'number' have no overlap.
				data.status?.categoryId === StatusCategoryIds[StatusCategoryTypes.DONE];

			// show done items when expanding the done status group
			if (isStatusGroup && isDoneStatus && !isExpanded) {
				setHideDoneItems(false);
			}

			if (isExpanded && shouldExpandAllItems) {
				disableExpandCollapseAllItems();
			}
			setGroupExpanded(issueId, !isExpanded);
		}, [
			data,
			disableExpandCollapseAllItems,
			expandedGroups,
			groupByFieldId,
			issueId,
			setGroupExpanded,
			setHideDoneItems,
			shouldExpandAllItems,
		]);

		const handleAddInlineCreateRow = useCallback(
			(e: SyntheticEvent<HTMLButtonElement>) => {
				e.preventDefault();
				setRowCreatePosition({
					parentId: String(issueId),
				});
				if (setGroupExpanded) {
					setGroupExpanded(issueId, true);
				}
				fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'listRowCreateGroupItemIcon', {
					groupByFieldId,
				});
			},
			[setRowCreatePosition, issueId, setGroupExpanded, createAnalyticsEvent, groupByFieldId],
		);

		const checkIfHasIssueCreation = useCanCreateIssueInStatus();

		const canInlineCreate = useMemo(() => {
			if (!canCreateIssues) {
				return false;
			}

			if (groupByFieldId !== STATUS_TYPE) {
				return true;
			}

			const statusId = data.status?.id && getAriConfig(data.status.id).resourceId;
			if (!statusId) {
				return false;
			}

			return checkIfHasIssueCreation(statusId);
		}, [canCreateIssues, checkIfHasIssueCreation, data.status, groupByFieldId]);

		const CreateIssueComponent = useMemo(
			() =>
				canInlineCreate && (
					<CreateIssuePlusButtonWrapper>
						<CreateIssuePlusButton
							tooltip={formatMessage(messages.createIssue)}
							label={formatMessage(messages.addGroupItem)}
							onClick={handleAddInlineCreateRow}
							shouldDisplayBtn
						/>
					</CreateIssuePlusButtonWrapper>
				),
			[canInlineCreate, formatMessage, handleAddInlineCreateRow],
		);

		if (loading) {
			return (
				<LoadingRow
					key="loading-grouping-header-row"
					data-testid="business-list.ui.list-view.group-item.loading-row"
				>
					<LoadingWrapper key={data.key}>
						<LoadingIcon />
						<LoadingField />
					</LoadingWrapper>
				</LoadingRow>
			);
		}

		return (
			<Wrapper>
				<SelectGroupCheckbox group={data} rowIndex={rowIndex} />

				<GroupContainer>
					{totalGroupItems > 0 && (
						<ExpandIcon
							onClick={onExpand}
							totalGroupItems={totalGroupItems}
							rowIndex={rowIndex}
							expanded={expandedGroups[issueId] === true}
						/>
					)}
					{field ? (
						<GroupItemRenderer field={field} data={data} />
					) : (
						<>
							{groupByFieldId === ASSIGNEE_TYPE && <AssigneeItem data={data} />}
							{groupByFieldId === STATUS_TYPE && <StatusItem data={data} />}
							{groupByFieldId === PRIORITY_TYPE && <PriorityItem data={data} />}
							{groupByFieldId === categoryFieldId && <CategoryItem data={data} />}
						</>
					)}
					{CreateIssueComponent}
				</GroupContainer>
			</Wrapper>
		);
	},
);
